import { useEffect, useState } from 'react';
import { collection, query, orderBy, limit, getDocs, where, Timestamp } from 'firebase/firestore';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
  Filler,
} from 'chart.js';

import { db } from '../../firebase';
import './index.scss';

ChartJS.register(
  LineElement, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  Title, 
  Tooltip, 
  Legend,
  BarElement,
  ArcElement,
  Filler
);

const AnalyticsDashboard = () => {
  const [pageViews, setPageViews] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [uniqueVisitors, setUniqueVisitors] = useState([]);
  const [visits, setVisits] = useState([]);
  const [stats, setStats] = useState({
    totalPageViews: 0,
    totalClicks: 0,
    uniqueVisitors: 0,
    totalVisits: 0,
    topPages: [],
    topClicks: [],
    topCountries: [],
    topCities: [],
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(7); // days
  const [visitorTrends, setVisitorTrends] = useState([]);
  const [metricType, setMetricType] = useState('visitors');
  const [geoData, setGeoData] = useState([]);
  const [chartType, setChartType] = useState('line');
  const [showComparison, setShowComparison] = useState(false);
  const [analyticsTab, setAnalyticsTab] = useState('overview');
  const [allUniqueVisitors, setAllUniqueVisitors] = useState([]);
  const [filteredVisitors, setFilteredVisitors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('lastVisit');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedVisitor, setSelectedVisitor] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  useEffect(() => {
    fetchAllUniqueVisitors();
  }, []);

  useEffect(() => {
    filterAndSortVisitors();
  }, [allUniqueVisitors, searchTerm, sortBy, sortOrder]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - timeRange);
      
      // Fetch unique visitors
      const uniqueVisitorsQuery = query(
        collection(db, 'unique_visitors'),
        orderBy('createdAt', 'desc')
      );
      const uniqueVisitorsSnapshot = await getDocs(uniqueVisitorsQuery);
      const allUniqueVisitors = uniqueVisitorsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Filter unique visitors by time range
      const filteredUniqueVisitors = allUniqueVisitors.filter(visitor => {
        const createdAt = visitor.createdAt?.toDate ? visitor.createdAt.toDate() : new Date(visitor.createdAt);
        return createdAt >= cutoffDate;
      });
      setUniqueVisitors(filteredUniqueVisitors);

      // Fetch visits
      const visitsQuery = query(
        collection(db, 'visits'),
        orderBy('createdAt', 'desc')
      );
      const visitsSnapshot = await getDocs(visitsQuery);
      const allVisits = visitsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Filter visits by time range
      const filteredVisits = allVisits.filter(visit => {
        const createdAt = visit.createdAt?.toDate ? visit.createdAt.toDate() : new Date(visit.createdAt);
        return createdAt >= cutoffDate;
      });
      setVisits(filteredVisits);

      // Fetch page views
      const pageViewsQuery = query(
        collection(db, 'page_views'),
        orderBy('createdAt', 'desc')
      );
      const pageViewsSnapshot = await getDocs(pageViewsQuery);
      const allPageViewsData = pageViewsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      const pageViewsData = allPageViewsData.filter(pv => {
        const createdAt = pv.createdAt?.toDate ? pv.createdAt.toDate() : new Date(pv.createdAt);
        return createdAt >= cutoffDate;
      });
      setPageViews(pageViewsData.slice(0, 100));

      // Fetch clicks
      const clicksQuery = query(
        collection(db, 'clicks'),
        orderBy('createdAt', 'desc')
      );
      const clicksSnapshot = await getDocs(clicksQuery);
      const allClicksData = clicksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      const clicksData = allClicksData.filter(click => {
        const createdAt = click.createdAt?.toDate ? click.createdAt.toDate() : new Date(click.createdAt);
        return createdAt >= cutoffDate;
      });
      setClicks(clicksData.slice(0, 100));

      // Calculate statistics based on new structure
      const pageCounts = {};
      const clickCounts = {};
      const countryCounts = {};
      const cityCounts = {};

      pageViewsData.forEach(pv => {
        pageCounts[pv.pagePath] = (pageCounts[pv.pagePath] || 0) + 1;
      });

      clicksData.forEach(click => {
        const key = click.elementText || click.elementId || click.elementType;
        clickCounts[key] = (clickCounts[key] || 0) + 1;
      });

      // Count countries and cities from unique visitors
      filteredUniqueVisitors.forEach(visitor => {
        if (visitor.country) {
          const countryKey = `${visitor.country} ${visitor.countryCode ? `(${visitor.countryCode})` : ''}`;
          countryCounts[countryKey] = (countryCounts[countryKey] || 0) + (visitor.totalVisits || 1);
        }
        
        if (visitor.city && visitor.country) {
          const cityKey = `${visitor.city}, ${visitor.country}`;
          cityCounts[cityKey] = (cityCounts[cityKey] || 0) + (visitor.totalVisits || 1);
        }
      });

      const topPages = Object.entries(pageCounts)
        .map(([path, count]) => ({ path, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      const topClicks = Object.entries(clickCounts)
        .map(([element, count]) => ({ element, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      const topCountries = Object.entries(countryCounts)
        .map(([country, count]) => ({ country, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      const topCities = Object.entries(cityCounts)
        .map(([city, count]) => ({ city, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Prepare geolocation data from unique visitors
      const geoLocationData = filteredUniqueVisitors
        .filter(visitor => visitor.latitude && visitor.longitude && visitor.ipAddress !== 'unknown')
        .map(visitor => ({
          lat: visitor.latitude,
          lng: visitor.longitude,
          country: visitor.country,
          city: visitor.city,
          ipAddress: visitor.ipAddress,
          visitorId: visitor.visitorId,
          totalVisits: visitor.totalVisits || 1,
          firstVisit: visitor.firstVisit,
          lastVisit: visitor.lastVisit
        }));

      setGeoData(geoLocationData);

      setStats({
        totalPageViews: pageViewsData.length,
        totalClicks: clicksData.length,
        uniqueVisitors: filteredUniqueVisitors.length,
        totalVisits: filteredVisits.length + filteredUniqueVisitors.length, // Include first visits
        topPages,
        topClicks,
        topCountries,
        topCities,
      });
      
      // Compute Daily Trends with new data structure
      const trends = computeDailyTrends(filteredUniqueVisitors, filteredVisits, pageViewsData, clicksData);
      setVisitorTrends(trends);

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUniqueVisitors = async () => {
    try {
      const visitorsQuery = query(
        collection(db, 'unique_visitors'),
        orderBy('createdAt', 'desc')
      );
      
      const visitorsSnapshot = await getDocs(visitorsQuery);
      const visitorsData = visitorsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      setAllUniqueVisitors(visitorsData);
    } catch (error) {
      console.error('Error fetching all unique visitors:', error);
    }
  };

  const filterAndSortVisitors = () => {
    let filtered = allUniqueVisitors;

    // Search filter
    if (searchTerm) {
      filtered = allUniqueVisitors.filter(visitor => 
        visitor.ipAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        visitor.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        visitor.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        visitor.isp?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        visitor.visitorId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle timestamp fields
      if (sortBy === 'firstVisit' || sortBy === 'lastVisit' || sortBy === 'createdAt') {
        aVal = aVal?.toDate ? aVal.toDate() : new Date(aVal || 0);
        bVal = bVal?.toDate ? bVal.toDate() : new Date(bVal || 0);
      }

      // Handle numeric fields
      if (sortBy === 'totalVisits') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      }

      // Handle string fields
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredVisitors(filtered);
  };

  const computeDailyTrends = (uniqueVisitors, visits, pageViewsData, clicksData) => {
    const dailyData = {};
    
    // Process unique visitors (first visits)
    uniqueVisitors.forEach(visitor => {
      const createdAt = visitor.createdAt?.toDate ? visitor.createdAt.toDate() : new Date(visitor.createdAt);
      const dateKey = createdAt.toISOString().split('T')[0];
      if (!dailyData[dateKey]) dailyData[dateKey] = { 
        uniqueVisitors: new Set(), 
        views: 0, 
        clicks: 0, 
        visits: 0 
      };
      dailyData[dateKey].uniqueVisitors.add(visitor.ipAddress);
      dailyData[dateKey].visits += 1; // First visit
    });

    // Process return visits
    visits.forEach(visit => {
      const createdAt = visit.createdAt?.toDate ? visit.createdAt.toDate() : new Date(visit.createdAt);
      const dateKey = createdAt.toISOString().split('T')[0];
      if (!dailyData[dateKey]) dailyData[dateKey] = { 
        uniqueVisitors: new Set(), 
        views: 0, 
        clicks: 0, 
        visits: 0 
      };
      dailyData[dateKey].uniqueVisitors.add(visit.ipAddress);
      dailyData[dateKey].visits += 1;
    });

    // Process page views
    pageViewsData.forEach(pv => {
      const createdAt = pv.createdAt?.toDate ? pv.createdAt.toDate() : new Date(pv.createdAt);
      const dateKey = createdAt.toISOString().split('T')[0];
      if (!dailyData[dateKey]) dailyData[dateKey] = { 
        uniqueVisitors: new Set(), 
        views: 0, 
        clicks: 0, 
        visits: 0 
      };
      dailyData[dateKey].views++;
    });

    // Process clicks
    clicksData.forEach(click => {
      const createdAt = click.createdAt?.toDate ? click.createdAt.toDate() : new Date(click.createdAt);
      const dateKey = createdAt.toISOString().split('T')[0];
      if (!dailyData[dateKey]) dailyData[dateKey] = { 
        uniqueVisitors: new Set(), 
        views: 0, 
        clicks: 0, 
        visits: 0 
      };
      dailyData[dateKey].clicks++;
    });

    const sortedDates = Object.keys(dailyData).sort();
    return sortedDates.map(date => ({
      date,
      visitors: dailyData[date].uniqueVisitors.size,
      views: dailyData[date].views,
      clicks: dailyData[date].clicks,
      visits: dailyData[date].visits,
    }));
  };

  const formatChartDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (timeRange <= 7) {
      // Show day of week for week view
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    } else if (timeRange <= 30) {
      // Show month/day for month view
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      // Show month/year for longer periods
      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }
  };

  const getEnhancedChartData = () => {
    const labels = visitorTrends.map(v => formatChartDate(v.date));
    
    if (showComparison) {
      // Multi-dataset comparison
      return {
        labels,
        datasets: [
          {
            label: 'Unique Visitors',
            data: visitorTrends.map(v => v.visitors),
            borderColor: '#FFD700',
            backgroundColor: 'rgba(255, 215, 0, 0.1)',
            pointBackgroundColor: '#FFD700',
            pointBorderColor: '#FFD700',
            pointHoverBackgroundColor: '#FFED4E',
            pointHoverBorderColor: '#FFD700',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            tension: 0.4,
            fill: true,
          },
          {
            label: 'Page Views',
            data: visitorTrends.map(v => v.views),
            borderColor: '#00D4FF',
            backgroundColor: 'rgba(0, 212, 255, 0.1)',
            pointBackgroundColor: '#00D4FF',
            pointBorderColor: '#00D4FF',
            pointHoverBackgroundColor: '#33E0FF',
            pointHoverBorderColor: '#00D4FF',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            tension: 0.4,
            fill: true,
          },
          {
            label: 'Total Visits',
            data: visitorTrends.map(v => v.visits),
            borderColor: '#FF6B6B',
            backgroundColor: 'rgba(255, 107, 107, 0.1)',
            pointBackgroundColor: '#FF6B6B',
            pointBorderColor: '#FF6B6B',
            pointHoverBackgroundColor: '#FF8E8E',
            pointHoverBorderColor: '#FF6B6B',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            tension: 0.4,
            fill: true,
          },
          {
            label: 'Clicks',
            data: visitorTrends.map(v => v.clicks),
            borderColor: '#4ECDC4',
            backgroundColor: 'rgba(78, 205, 196, 0.1)',
            pointBackgroundColor: '#4ECDC4',
            pointBorderColor: '#4ECDC4',
            pointHoverBackgroundColor: '#70D7CE',
            pointHoverBorderColor: '#4ECDC4',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            tension: 0.4,
            fill: true,
          }
        ],
      };
    } else {
      // Single metric with enhanced styling
      const data = metricType === 'visitors'
        ? visitorTrends.map(v => v.visitors)
        : metricType === 'views'
          ? visitorTrends.map(v => v.views)
          : metricType === 'visits'
            ? visitorTrends.map(v => v.visits)
            : visitorTrends.map(v => v.clicks);

      const colors = {
        visitors: { border: '#FFD700', bg: 'rgba(255, 215, 0, 0.2)' },
        views: { border: '#00D4FF', bg: 'rgba(0, 212, 255, 0.2)' },
        visits: { border: '#FF6B6B', bg: 'rgba(255, 107, 107, 0.2)' },
        clicks: { border: '#4ECDC4', bg: 'rgba(78, 205, 196, 0.2)' }
      };

      const color = colors[metricType] || colors.visitors;

      return {
        labels,
        datasets: [
          {
            label: metricType === 'visitors' ? 'Unique Visitors' :
                   metricType === 'views' ? 'Page Views' :
                   metricType === 'visits' ? 'Total Visits' : 'Clicks',
            data,
            borderColor: color.border,
            backgroundColor: color.bg,
            pointBackgroundColor: color.border,
            pointBorderColor: color.border,
            pointHoverBackgroundColor: '#FFFFFF',
            pointHoverBorderColor: color.border,
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 8,
            tension: 0.4,
            fill: true,
            borderWidth: 3,
          },
        ],
      };
    }
  };

  const getChartOptions = () => {
    const maxValue = Math.max(
      ...visitorTrends.flatMap(v => [v.visitors, v.views, v.visits, v.clicks])
    );

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          labels: { 
            color: '#fff',
            font: {
              size: 14,
              weight: 'bold'
            },
            padding: 20,
            usePointStyle: true,
            pointStyle: 'circle'
          },
          position: 'top',
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#FFD700',
          bodyColor: '#fff',
          borderColor: '#FFD700',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          callbacks: {
            title: function(context) {
              const date = new Date(visitorTrends[context[0].dataIndex].date);
              return date.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              });
            },
            label: function(context) {
              return `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: { 
            color: '#fff',
            font: {
              size: 12,
              weight: 'bold'
            },
            maxRotation: 45,
            minRotation: 0
          },
          grid: { 
            color: 'rgba(255,255,255,0.1)',
            drawBorder: false
          },
          border: {
            color: 'rgba(255, 215, 0, 0.3)'
          }
        },
        y: {
          ticks: { 
            color: '#fff',
            font: {
              size: 12,
              weight: 'bold'
            },
            callback: function(value) {
              return value.toLocaleString();
            }
          },
          grid: { 
            color: 'rgba(255,255,255,0.1)',
            drawBorder: false
          },
          border: {
            color: 'rgba(255, 215, 0, 0.3)'
          },
          beginAtZero: true,
          max: Math.ceil(maxValue * 1.1) // Add 10% padding
        },
      },
      elements: {
        line: {
          borderJoinStyle: 'round',
          borderCapStyle: 'round'
        }
      },
      animation: {
        duration: 2000,
        easing: 'easeInOutQuart'
      }
    };
  };

  const getTopCountriesChartData = () => {
    const topCountriesData = stats.topCountries.slice(0, 5);
    return {
      labels: topCountriesData.map(country => country.country.split(' ')[0]),
      datasets: [{
        data: topCountriesData.map(country => country.count),
        backgroundColor: [
          'rgba(255, 215, 0, 0.8)',
          'rgba(0, 212, 255, 0.8)',
          'rgba(255, 107, 107, 0.8)',
          'rgba(78, 205, 196, 0.8)',
          'rgba(255, 159, 64, 0.8)'
        ],
        borderColor: [
          '#FFD700',
          '#00D4FF',
          '#FF6B6B',
          '#4ECDC4',
          '#FF9F40'
        ],
        borderWidth: 2,
        hoverBorderWidth: 3,
        hoverBackgroundColor: [
          'rgba(255, 215, 0, 1)',
          'rgba(0, 212, 255, 1)',
          'rgba(255, 107, 107, 1)',
          'rgba(78, 205, 196, 1)',
          'rgba(255, 159, 64, 1)'
        ]
      }]
    };
  };

  const renderChart = () => {
    const chartData = getEnhancedChartData();
    const options = getChartOptions();

    if (chartType === 'bar') {
      return <Bar data={chartData} options={options} />;
    } else {
      return <Line data={chartData} options={options} />;
    }
  };

  const getChartData = () => {
    const labels = visitorTrends.map(v => v.date);
    const data =
      metricType === 'visitors'
        ? visitorTrends.map(v => v.visitors)
        : metricType === 'views'
          ? visitorTrends.map(v => v.views)
          : metricType === 'visits'
            ? visitorTrends.map(v => v.visits)
            : visitorTrends.map(v => v.clicks);

    const label =
      metricType === 'visitors'
        ? 'Unique Visitors'
        : metricType === 'views'
          ? 'Page Views'
          : metricType === 'visits'
            ? 'Total Visits'
            : 'Clicks';

    return {
      labels,
      datasets: [
        {
          label,
          data,
          fill: true,
          backgroundColor: 'rgba(255,215,0,0.1)',
          borderColor: '#FFD700',
          pointBackgroundColor: '#FFD700',
          tension: 0.3,
        },
      ],
    };
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMinutes > 0) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const getLocationString = (pageView) => {
    if (pageView.city && pageView.country) {
      return `${pageView.city}, ${pageView.country}`;
    } else if (pageView.country) {
      return pageView.country;
    } else if (pageView.ipAddress && pageView.ipAddress !== 'unknown') {
      return pageView.ipAddress;
    }
    return 'Unknown';
  };

  const getLocationStringForVisitor = (visitor) => {
    const parts = [];
    if (visitor.city) parts.push(visitor.city);
    if (visitor.region) parts.push(visitor.region);
    if (visitor.country) parts.push(visitor.country);
    return parts.join(', ') || 'Unknown Location';
  };

  const getDeviceInfo = (visitor) => {
    if (!visitor.userAgents || visitor.userAgents.length === 0) return 'Unknown Device';
    
    const userAgent = visitor.userAgents[0];
    const platform = visitor.platforms?.[0] || 'Unknown Platform';
    
    // Simple device detection
    if (userAgent.includes('Mobile') || userAgent.includes('Android')) return `📱 Mobile (${platform})`;
    if (userAgent.includes('iPad') || userAgent.includes('Tablet')) return `📱 Tablet (${platform})`;
    return `💻 Desktop (${platform})`;
  };

  const getBrowserInfo = (visitor) => {
    if (!visitor.userAgents || visitor.userAgents.length === 0) return 'Unknown Browser';
    
    const userAgent = visitor.userAgents[0];
    
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    if (userAgent.includes('Opera')) return 'Opera';
    
    return 'Other Browser';
  };

  const getCountryFlag = (countryCode) => {
    if (!countryCode) return '🌍';
    return String.fromCodePoint(...countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt(0)));
  };

  if (loading) {
    return (
      <div className="analytics-dashboard">
        <div className="loading">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <div className="dashboard-header">
        <div className="analytics-tabs">
          <button
            className={analyticsTab === 'overview' ? 'active' : ''}
            onClick={() => setAnalyticsTab('overview')}
          >
            📊 Overview
          </button>
          <button
            className={analyticsTab === 'visitors' ? 'active' : ''}
            onClick={() => setAnalyticsTab('visitors')}
          >
            👥 Unique Visitors ({allUniqueVisitors.length})
          </button>
        </div>
        
        {analyticsTab === 'overview' && (
          <div className="time-range-selector">
            <label>Time Range: </label>
            <select value={timeRange} onChange={(e) => setTimeRange(Number(e.target.value))}>
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        )}
      </div>

      {analyticsTab === 'overview' && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Page Views</h3>
              <p className="stat-value">{stats.totalPageViews.toLocaleString()}</p>
              <small>Last {timeRange} day{timeRange !== 1 ? 's' : ''}</small>
            </div>
            <div className="stat-card">
              <h3>Total Clicks</h3>
              <p className="stat-value">{stats.totalClicks.toLocaleString()}</p>
              <small>All interactions</small>
            </div>
            <div className="stat-card">
              <h3>Unique Visitors</h3>
              <p className="stat-value">{stats.uniqueVisitors.toLocaleString()}</p>
              <small>By IP address</small>
            </div>
            <div className="stat-card">
              <h3>Countries</h3>
              <p className="stat-value">{stats.topCountries.length}</p>
              <small>Unique countries</small>
            </div>
          </div>

          <div className="analytics-sections">
            <div className="section">
              <h2>Top Countries</h2>
              <div className="list-container">
                {stats.topCountries.length > 0 ? (
                  <ul className="analytics-list">
                    {stats.topCountries.map((country, idx) => (
                      <li key={idx}>
                        <span className="country-name">
                          {getCountryFlag(country.country.match(/\(([^)]+)\)$/)?.[1])} {country.country}
                        </span>
                        <span className="count">{country.count} visits</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No location data yet</p>
                )}
              </div>
            </div>

            <div className="section">
              <h2>Top Cities</h2>
              <div className="list-container">
                {stats.topCities.length > 0 ? (
                  <ul className="analytics-list">
                    {stats.topCities.map((city, idx) => (
                      <li key={idx}>
                        <span className="city-name">📍 {city.city}</span>
                        <span className="count">{city.count} visits</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No city data yet</p>
                )}
              </div>
            </div>

            <div className="section">
              <h2>Top Pages</h2>
              <div className="list-container">
                {stats.topPages.length > 0 ? (
                  <ul className="analytics-list">
                    {stats.topPages.map((page, idx) => (
                      <li key={idx}>
                        <span className="page-path">{page.path}</span>
                        <span className="count">{page.count} views</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No page views yet</p>
                )}
              </div>
            </div>
          </div>

          <div className="chart-section">
            <div className="chart-header">
              <h2>Trends Over Time</h2>
              <div className="chart-controls">
                <div className="metric-toggle">
                  <label>Metric: </label>
                  <select 
                    value={metricType} 
                    onChange={(e) => setMetricType(e.target.value)}
                    disabled={showComparison}
                  >
                    <option value="visitors">Unique Visitors</option>
                    <option value="views">Page Views</option>
                    <option value="visits">Total Visits</option>
                    <option value="clicks">Clicks</option>
                  </select>
                </div>
                
                <div className="chart-type-toggle">
                  <label>Type: </label>
                  <select value={chartType} onChange={(e) => setChartType(e.target.value)}>
                    <option value="line">Line Chart</option>
                    <option value="bar">Bar Chart</option>
                  </select>
                </div>
                
                <div className="comparison-toggle">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={showComparison}
                      onChange={(e) => setShowComparison(e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    Compare All Metrics
                  </label>
                </div>
              </div>
            </div>
            
            <div className="chart-container">
              {renderChart()}
            </div>
            
            {showComparison && (
              <div className="chart-legend">
                <p>💡 <strong>Tip:</strong> Comparison mode shows all metrics together. Use the legend to toggle individual datasets on/off.</p>
              </div>
            )}
          </div>

          {stats.topCountries.length > 0 && (
            <div className="chart-section">
              <div className="chart-header">
                <h2>Top 5 Countries Distribution</h2>
              </div>
              <div className="chart-container doughnut-container">
                <Doughnut 
                  data={getTopCountriesChartData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right',
                        labels: {
                          color: '#fff',
                          font: { size: 14, weight: 'bold' },
                          padding: 15,
                          usePointStyle: true
                        }
                      },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: '#FFD700',
                        bodyColor: '#fff',
                        borderColor: '#FFD700',
                        borderWidth: 1,
                        cornerRadius: 8,
                        callbacks: {
                          label: function(context) {
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percentage = ((context.parsed / total) * 100).toFixed(1);
                            return `${context.label}: ${context.parsed} visits (${percentage}%)`;
                          }
                        }
                      }
                    },
                    animation: {
                      animateRotate: true,
                      animateScale: true,
                      duration: 2000
                    }
                  }}
                />
              </div>
            </div>
          )}

          <div className="analytics-sections">
            <div className="section">
              <h2>Recent Page Views</h2>
              <div className="table-container">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Page</th>
                      <th>Visitor</th>
                      <th>Location</th>
                      <th>Device</th>
                      <th>Referrer</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageViews.slice(0, 20).map((pv) => (
                      <tr key={pv.id}>
                        <td>{pv.pagePath}</td>
                        <td className="visitor-info">
                          <div className="ip-address">{pv.ipAddress || 'N/A'}</div>
                          <small className="visitor-id">{pv.visitorId?.substring(0, 12)}...</small>
                        </td>
                        <td className="location-info">
                          <div className="location">
                            {pv.countryCode && getCountryFlag(pv.countryCode)} {getLocationString(pv)}
                          </div>
                          {pv.isp && <small className="isp">ISP: {pv.isp}</small>}
                        </td>
                        <td>
                          <div>{pv.platform}</div>
                          <small style={{ color: '#999', fontSize: '0.8em' }}>
                            {pv.screenWidth}x{pv.screenHeight}
                          </small>
                        </td>
                        <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {pv.referrer === 'direct' ? 'Direct' : pv.referrer?.substring(0, 30) || 'Direct'}
                        </td>
                        <td>{formatDate(pv.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="section">
              <h2>Top Clicked Elements</h2>
              <div className="list-container">
                {stats.topClicks.length > 0 ? (
                  <ul className="analytics-list">
                    {stats.topClicks.map((click, idx) => (
                      <li key={idx}>
                        <span className="element-name">{click.element.substring(0, 50)}</span>
                        <span className="count">{click.count} clicks</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No clicks tracked yet</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {analyticsTab === 'visitors' && (
        <div className="unique-visitors-section">
          <div className="visitors-header">
            <div className="summary">
              <span className="count">{allUniqueVisitors.length} Total Visitors</span>
              <span className="filtered">({filteredVisitors.length} shown)</span>
            </div>
          </div>

          <div className="visitors-controls">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search by IP, country, city, ISP, or visitor ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="sort-controls">
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="lastVisit">Last Visit</option>
                <option value="firstVisit">First Visit</option>
                <option value="totalVisits">Total Visits</option>
                <option value="country">Country</option>
                <option value="ipAddress">IP Address</option>
                <option value="createdAt">Date Added</option>
              </select>
              
              <button 
                className={`sort-order ${sortOrder}`}
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>

          <div className="visitors-grid">
            {filteredVisitors.map((visitor) => (
              <div 
                key={visitor.id} 
                className={`visitor-card ${selectedVisitor?.id === visitor.id ? 'selected' : ''}`}
                onClick={() => setSelectedVisitor(selectedVisitor?.id === visitor.id ? null : visitor)}
              >
                <div className="visitor-header">
                  <div className="visitor-id">
                    <strong>ID:</strong> {visitor.visitorId}
                  </div>
                  <div className="visitor-stats">
                    <span className="visits-count">{visitor.totalVisits || 1} visits</span>
                    <span className="last-seen">{formatRelativeTime(visitor.lastVisit)}</span>
                  </div>
                </div>

                <div className="visitor-basic-info">
                  <div className="location">
                    <span className="flag">{getCountryFlag(visitor.countryCode)}</span>
                    <span className="location-text">{getLocationStringForVisitor(visitor)}</span>
                  </div>
                  
                  <div className="ip-address">
                    <strong>IP:</strong> {visitor.ipAddress}
                  </div>
                  
                  <div className="device-info">
                    {getDeviceInfo(visitor)} • {getBrowserInfo(visitor)}
                  </div>
                </div>

                {selectedVisitor?.id === visitor.id && (
                  <div className="visitor-details">
                    <div className="details-section">
                      <h3>📍 Location Details</h3>
                      <div className="details-grid">
                        <div><strong>Country:</strong> {visitor.country || 'Unknown'}</div>
                        <div><strong>Region:</strong> {visitor.region || 'Unknown'}</div>
                        <div><strong>City:</strong> {visitor.city || 'Unknown'}</div>
                        <div><strong>Coordinates:</strong> {visitor.latitude && visitor.longitude ? 
                          `${visitor.latitude.toFixed(4)}, ${visitor.longitude.toFixed(4)}` : 'Unknown'}</div>
                        <div><strong>Timezone:</strong> {visitor.timezoneFromIP || 'Unknown'}</div>
                        <div><strong>ISP:</strong> {visitor.isp || 'Unknown'}</div>
                      </div>
                    </div>

                    <div className="details-section">
                      <h3>🕒 Visit Timeline</h3>
                      <div className="details-grid">
                        <div><strong>First Visit:</strong> {formatDate(visitor.firstVisit)}</div>
                        <div><strong>Last Visit:</strong> {formatDate(visitor.lastVisit)}</div>
                        <div><strong>Total Visits:</strong> {visitor.totalVisits || 1}</div>
                        <div><strong>Record Created:</strong> {formatDate(visitor.createdAt)}</div>
                      </div>
                    </div>

                    <div className="details-section">
                      <h3>🖥️ Device & Browser Info</h3>
                      <div className="details-grid">
                        <div><strong>Platforms:</strong> {visitor.platforms?.join(', ') || 'Unknown'}</div>
                        <div><strong>Languages:</strong> {visitor.languages?.join(', ') || 'Unknown'}</div>
                      </div>
                      {visitor.userAgents && visitor.userAgents.length > 0 && (
                        <div className="user-agents">
                          <strong>User Agents:</strong>
                          {visitor.userAgents.map((ua, idx) => (
                            <div key={idx} className="user-agent">{ua}</div>
                          ))}
                        </div>
                      )}
                    </div>

                    {visitor.visits && visitor.visits.length > 0 && (
                      <div className="details-section">
                        <h3>📊 Visit History ({visitor.visits.length} sessions)</h3>
                        <div className="visits-list">
                          {visitor.visits.slice(0, 5).map((visit, idx) => (
                            <div key={idx} className="visit-item">
                              <div><strong>Session:</strong> {visit.sessionId}</div>
                              <div><strong>Time:</strong> {formatDate(visit.visitTime)}</div>
                              <div><strong>Screen:</strong> {visit.screenWidth}×{visit.screenHeight}</div>
                              <div><strong>Referrer:</strong> {visit.referrer || 'Direct'}</div>
                            </div>
                          ))}
                          {visitor.visits.length > 5 && (
                            <div className="more-visits">... and {visitor.visits.length - 5} more visits</div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="details-section">
                      <h3>🔗 Technical IDs</h3>
                      <div className="technical-ids">
                        <div><strong>Document ID:</strong> {visitor.id}</div>
                        <div><strong>Visitor ID:</strong> {visitor.visitorId}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredVisitors.length === 0 && (
            <div className="no-results">
              <p>No visitors found matching your search criteria.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;

